/**
 * Wie breit das angedockte Dokument ist — rein, ohne DOM, damit es sich prüfen lässt.
 *
 * Gerechnet wird mit einem **Anteil** am Platz neben der Sidebar, nicht mit Pixeln: Derselbe
 * Nutzer sitzt mal am 14-Zoll-Laptop (rund 1440 px), mal am Monitor, und 50 % sollen auf beiden
 * 50 % bleiben. Zwei Untergrenzen: Unter 420 px ist eine A4-Seite nicht mehr lesbar, unter 380 px
 * bricht der Chat jede Tabellenzeile.
 */

export const DOCK_MIN_DOCUMENT_PX = 420;
export const DOCK_MIN_CHAT_PX = 380;
export const DOCK_DEFAULT_SHARE = 0.5;
/** Die eingeklappte Sidebar (`--sidebar-width-icon`, 4.25rem). */
export const SIDEBAR_RAIL_PX = 68;

/** Der Platz für Chat und Dokument zusammen. */
export function dockAvailableWidth(viewportWidth: number, sidebarOpen: boolean, sidebarWidth: number): number {
    return Math.max(0, viewportWidth - (sidebarOpen ? sidebarWidth : SIDEBAR_RAIL_PX));
}

/**
 * Die Breite des Dokuments in Pixeln. Reicht der Platz nicht für beide Untergrenzen, teilen sie
 * sich ihn hälftig — besser zwei enge Spalten als eine, die verschwindet.
 */
export function dockWidth(available: number, share: number): number {
    const wanted = Math.round(available * (Number.isFinite(share) ? share : DOCK_DEFAULT_SHARE));
    const max = available - DOCK_MIN_CHAT_PX;
    if (max < DOCK_MIN_DOCUMENT_PX) return Math.round(available / 2);
    return Math.min(max, Math.max(DOCK_MIN_DOCUMENT_PX, wanted));
}

/** Der Anteil, wenn der Ziehgriff bei `pointerX` steht (das Dokument reicht bis zum rechten Rand). */
export function shareAt(pointerX: number, viewportWidth: number, available: number): number {
    if (available <= 0) return DOCK_DEFAULT_SHARE;
    return Math.min(0.85, Math.max(0.15, (viewportWidth - pointerX) / available));
}

const SHARE_KEY = 'documentViewer.dockShare';

/** Der zuletzt gezogene Anteil — je Browser, nicht je Nutzer; fehlt er, die Hälfte. */
export function readDockShare(): number {
    try {
        const stored = Number(window.localStorage.getItem(SHARE_KEY));
        return stored > 0 && stored < 1 ? stored : DOCK_DEFAULT_SHARE;
    } catch {
        return DOCK_DEFAULT_SHARE;
    }
}

export function writeDockShare(share: number): void {
    try {
        window.localStorage.setItem(SHARE_KEY, String(share));
    } catch {
        /* privater Modus: dann eben nicht gemerkt */
    }
}
